import { useState } from 'react';
import { Download, Twitter, Instagram, Youtube, Facebook, ExternalLink, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PDF_URL = "https://customer-assets.emergentagent.com/job_f3838741-0ee2-4107-b608-00dc3222a68a/artifacts/i6nycba6_5%20Truths%20About%20The%20School%20To%20Prison%20Pipeline.pdf";
const BOOK_COVER_URL = "https://customer-assets.emergentagent.com/job_f3838741-0ee2-4107-b608-00dc3222a68a/artifacts/iocew1to_The%20school%20to%20prison%20pipeline.png";

const EbookDownload = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('Please enter your first name');
      return false;
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address');
      return false;
    }
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/leads`, {
        first_name: formData.first_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.replace(/\D/g, '')
      });
      setIsUnlocked(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = PDF_URL;
    link.download = '5 Truths About The School To Prison Pipeline.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen radial-gradient-bg relative overflow-hidden" data-testid="ebook-download-page">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Main Content */}
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Text Content - Left Side */}
              <div className="order-2 lg:order-1 text-center lg:text-left">
                <span className="font-mono text-xs tracking-widest text-slate-500 uppercase mb-4 block">
                  Free Download
                </span>
                <h1 
                  className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight leading-tight mb-6"
                  data-testid="hero-headline"
                >
                  Your Free Ebook Is Ready
                </h1>
                <p 
                  className="text-base lg:text-lg text-slate-400 leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0"
                  data-testid="hero-subheadline"
                >
                  Download "<span className="text-slate-200 font-medium">5 Truths About The School To Prison Pipeline</span>" 
                  and learn how school discipline policies push students from classrooms into the criminal justice system.
                </p>
                
                {/* Lead Capture Form or Download Button */}
                {!isUnlocked ? (
                  <div className="card-glass rounded-sm p-6 lg:p-8 max-w-md mx-auto lg:mx-0" data-testid="lead-capture-form">
                    <h3 className="font-heading text-xl text-slate-50 mb-2">Get Instant Access</h3>
                    <p className="text-slate-400 text-sm mb-6">Enter your details to unlock your free copy</p>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name" className="text-slate-300 text-sm">First Name</Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          type="text"
                          placeholder="Enter your first name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          className="bg-slate-950 border-slate-700 focus:border-slate-500 text-slate-100 placeholder:text-slate-600 h-12"
                          data-testid="input-first-name"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300 text-sm">Email Address</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="bg-slate-950 border-slate-700 focus:border-slate-500 text-slate-100 placeholder:text-slate-600 h-12"
                          data-testid="input-email"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300 text-sm">Mobile Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          className="bg-slate-950 border-slate-700 focus:border-slate-500 text-slate-100 placeholder:text-slate-600 h-12"
                          data-testid="input-phone"
                        />
                      </div>
                      
                      {error && (
                        <p className="text-red-400 text-sm" data-testid="form-error">{error}</p>
                      )}
                      
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full btn-primary bg-slate-50 text-slate-950 hover:bg-slate-200 rounded-sm font-semibold tracking-wide py-6 text-base h-auto mt-2"
                        data-testid="submit-button"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Download className="w-5 h-5 mr-2" />
                            Get My Free Ebook
                          </>
                        )}
                      </Button>
                    </form>
                    
                    <p className="text-slate-600 text-xs mt-4 text-center">
                      We respect your privacy and will never share your information.
                    </p>
                  </div>
                ) : (
                  <div className="card-glass rounded-sm p-6 lg:p-8 max-w-md mx-auto lg:mx-0 text-center" data-testid="download-unlocked">
                    <div className="flex items-center justify-center mb-4">
                      <CheckCircle className="w-12 h-12 text-green-400" />
                    </div>
                    <h3 className="font-heading text-xl text-slate-50 mb-2">Success!</h3>
                    <p className="text-slate-400 text-sm mb-6">Your download is ready. Click below to get your ebook.</p>
                    
                    <Button
                      onClick={handleDownload}
                      className="w-full btn-primary bg-slate-50 text-slate-950 hover:bg-slate-200 rounded-sm font-semibold tracking-wide py-6 text-base h-auto"
                      data-testid="download-button"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download The Ebook
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Book Cover - Right Side */}
              <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
                <div 
                  className="book-shadow rounded-sm overflow-hidden transform perspective-1000"
                  style={{ 
                    transform: 'perspective(1000px) rotateY(-10deg)',
                    maxWidth: '400px'
                  }}
                  data-testid="book-cover-container"
                >
                  <img 
                    src={BOOK_COVER_URL} 
                    alt="5 Truths About The School To Prison Pipeline - Book Cover"
                    className="w-full h-auto"
                    data-testid="book-cover-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Description Section */}
        <section className="py-16 lg:py-24 border-t border-slate-800/50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="card-glass rounded-sm p-8 lg:p-12" data-testid="description-section">
              <span className="font-mono text-xs tracking-widest text-slate-500 uppercase mb-6 block">
                About This Report
              </span>
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                This report exposes how suspensions, zero tolerance policies, and police presence in schools 
                contribute to the School To Prison Pipeline. The research connects education policies to mass 
                incarceration and offers insight into how communities can challenge the system.
              </p>
            </div>
          </div>
        </section>

        {/* Author Section */}
        <section className="py-16 lg:py-24 border-t border-slate-800/50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              {/* Author Image */}
              <div className="lg:col-span-2 flex justify-center">
                <div 
                  className="w-48 h-48 lg:w-64 lg:h-64 rounded-sm overflow-hidden border border-slate-700/50"
                  data-testid="author-image-container"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ebook-download-free/artifacts/hxjzl1qg_Director_Rahiem_Shabazz.jpg"
                    alt="Rahiem Shabazz"
                    className="w-full h-full object-cover"
                    data-testid="author-image"
                  />
                </div>
              </div>
              
              {/* Author Info */}
              <div className="lg:col-span-3 text-center lg:text-left" data-testid="author-section">
                <span className="font-mono text-xs tracking-widest text-slate-500 uppercase mb-4 block">
                  About The Author
                </span>
                <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight mb-4">
                  Rahiem Shabazz
                </h2>
                <p className="text-slate-400 text-base lg:text-lg leading-relaxed mb-2">
                  Creator of the Elementary Genocide documentary series
                </p>
                <p className="text-slate-500 text-base">
                  Author, filmmaker, and social justice advocate.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Follow Section */}
        <section className="py-16 lg:py-24 border-t border-slate-800/50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <span className="font-mono text-xs tracking-widest text-slate-500 uppercase mb-4 block">
              Stay Connected
            </span>
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-slate-50 tracking-tight mb-4">
              Follow Rahiem Shabazz
            </h3>
            <p className="text-slate-400 text-base mb-10 max-w-lg mx-auto">
              Stay updated on new documentaries, speaking engagements, and research.
            </p>
            
            {/* Social Icons */}
            <div className="flex justify-center items-center gap-8" data-testid="social-links">
              <a 
                href="https://x.com/rahiemshabazz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-slate-500 hover:text-slate-50"
                aria-label="Follow on Twitter"
                data-testid="twitter-link"
              >
                <Twitter className="w-7 h-7" />
              </a>
              <a 
                href="https://instagram.com/RahiemShabazz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-slate-500 hover:text-slate-50"
                aria-label="Follow on Instagram"
                data-testid="instagram-link"
              >
                <Instagram className="w-7 h-7" />
              </a>
              <a 
                href="https://www.youtube.com/@ShabazzRahiem" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-slate-500 hover:text-slate-50"
                aria-label="Follow on YouTube"
                data-testid="youtube-link"
              >
                <Youtube className="w-7 h-7" />
              </a>
              <a 
                href="https://facebook.com/RahiemShabazz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-slate-500 hover:text-slate-50"
                aria-label="Follow on Facebook"
                data-testid="facebook-link"
              >
                <Facebook className="w-7 h-7" />
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-slate-800/50">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <a 
              href="https://rahiemshabazz.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-300 transition-colors text-sm"
              data-testid="footer-link"
            >
              <ExternalLink className="w-4 h-4" />
              RahiemShabazz.com
            </a>
            <p className="text-slate-600 text-xs mt-4">
              © {new Date().getFullYear()} Rahiem Shabazz. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default EbookDownload;
